// Toaster.jsx
import { X } from 'lucide-react';
import React, { useState, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import './Toaster.css';

const TOAST_LIMIT = 1;
const TOAST_VISIBLE_MS = 5000;     // <-- stays visible this long
const TOAST_REMOVE_DELAY = 350;     // <-- removal after slide-out (matches CSS 300ms)


let count = 0;
function genId() { count = (count + 1) % Number.MAX_SAFE_INTEGER; return count.toString(); }

const actionTypes = {
  ADD_TOAST: 'ADD_TOAST',
  UPDATE_TOAST: 'UPDATE_TOAST',
  DISMISS_TOAST: 'DISMISS_TOAST',
  REMOVE_TOAST: 'REMOVE_TOAST',
};

const toastTimeouts = new Map();
const listeners = [];
let memoryState = { toasts: [] };
const removeTimers = new Map();     // after slide-out
const autoCloseTimers = new Map();  // while visible
function scheduleRemove(id) {
  if (removeTimers.has(id)) return;
  const t = setTimeout(() => {
    removeTimers.delete(id);
    dispatch({ type: actionTypes.REMOVE_TOAST, toastId: id });
  }, TOAST_REMOVE_DELAY);
  removeTimers.set(id, t);
}
function reducer(state, action) {
  switch (action.type) {
    case actionTypes.ADD_TOAST:
      return { ...state, toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT) };
    case actionTypes.UPDATE_TOAST:
      return { ...state, toasts: state.toasts.map(t => t.id === action.toast.id ? { ...t, ...action.toast } : t) };
      case actionTypes.DISMISS_TOAST: {
        const { toastId } = action;
        if (toastId) scheduleRemove(toastId);
        else state.toasts.forEach(t => scheduleRemove(t.id));
        return {
          ...state,
          toasts: state.toasts.map(t =>
            (toastId === undefined || t.id === toastId) ? { ...t, open: false } : t
          ),
        };
      }
  
    case actionTypes.REMOVE_TOAST:
      if (action.toastId === undefined) return { ...state, toasts: [] };
      return { ...state, toasts: state.toasts.filter(t => t.id !== action.toastId) };
    default:
      return state;
  }
}

function dispatch(action) {
  memoryState = reducer(memoryState, action);
  listeners.forEach(l => l(memoryState));
}
export function toast(props) {
  const id = genId();

  const update  = (next) => dispatch({ type: actionTypes.UPDATE_TOAST, toast: { ...next, id } });
  const dismiss = ()       => dispatch({ type: actionTypes.DISMISS_TOAST, toastId: id });

  // add open toast
  dispatch({
    type: actionTypes.ADD_TOAST,
    toast: { ...props, id, open: true, onOpenChange: (open) => { if (!open) dismiss(); } },
  });

  // schedule auto-close (allow override via props.duration; disable with Infinity/null)
  const duration = Number.isFinite(props?.duration) ? props.duration : TOAST_VISIBLE_MS;
  if (Number.isFinite(duration)) {
    const t = setTimeout(() => dismiss(), duration);
    autoCloseTimers.set(id, t);
  }

  return { id, update, dismiss };
}

export function useToast() {
  const [state, setState] = React.useState(memoryState);
  React.useEffect(() => {
    if (!listeners.includes(setState)) listeners.push(setState);
    return () => {
      const i = listeners.indexOf(setState);
      if (i > -1) listeners.splice(i, 1);
    };
  }, []); // subscribe once
  return { ...state, toast, dismiss: (toastId) => dispatch({ type: actionTypes.DISMISS_TOAST, toastId }) };
}

/* ---------- PORTAL UTIL ---------- */
function useToastRoot(id = 'toast-root') {
  const [el, setEl] = useState(null);
  useLayoutEffect(() => {
    let node = document.getElementById(id);
    if (!node) {
      node = document.createElement('div');
      node.id = id;
      document.body.appendChild(node);
    }
    setEl(node);
  }, [id]);
  return el;
}

/* ---------- PORTALIZED TOASTER ---------- */
export function Toaster() {
  const { toasts } = useToast();
  const root = useToastRoot(); // #toast-root (auto-created if missing)

  if (!root) return null;

  const ui = (
<div className="toaster">
  {toasts.map(t => (
    <div
      key={t.id}
      className={`toast ${t.variant === 'destructive' ? 'toast-destructive' : ''} ${t.open ? 'toast-open' : ''}`}
      role="status" aria-live="polite"
    >
      <div className="toast-header">
        <div className="toast-title">
          {t.icon && <span className="toast-icon">{t.icon}</span>}
          {t.title}
        </div>

        <button
          className="toast-close"
          type="button"
          aria-label="Close notification"
          onClick={() => t.onOpenChange(false)}
          title="Close"
        >
          <X size={18} />
        </button>
      </div>

      {t.description && <div className="toast-description">{t.description}</div>}

      {t.action && (
        <button className="toast-action" onClick={t.action.onClick}>
          {t.action.label}
        </button>
      )}
    </div>
  ))}
</div>

  );

  return createPortal(ui, root);
}
